import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Clock, Filter, Plus, Check, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import MarketSummary from '../components/MarketSummary';
import { usePortfolio } from '../hooks/usePortfolio';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function RecommendationsPage() {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const { togglePortfolio, isInPortfolio } = usePortfolio();

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/recommendations/latest?limit=500`);
      const data = await response.json();
      
      if (data.success) {
        setRecommendations(data.data);
        if (data.last_updated && data.last_updated.timestamp) {
          setLastUpdated(new Date(data.last_updated.timestamp));
        }
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 300) return 'text-green-500';
    if (score >= 200) return 'text-green-400';
    if (score >= 100) return 'text-yellow-500';
    return 'text-muted-foreground';
  };

  const getScoreBadge = (score) => {
    if (score >= 300) return 'badge-green';
    if (score >= 200) return 'badge-yellow';
    return 'badge-default';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading recommendations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button onClick={fetchRecommendations} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-background border-b border-border px-4 py-4">
        <div className="max-w-7xl mx-auto">
          {/* Market Summary */}
          <MarketSummary />
          
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-2xl font-bold font-display">ASX Top Picks</h1>
              <p className="text-sm text-muted-foreground">Highest scoring recommendations</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {lastUpdated ? (
                  <span data-testid="last-updated">
                    {formatDistanceToNow(lastUpdated, { addSuffix: true })}
                  </span>
                ) : (
                  <span>Loading...</span>
                )}
              </div>
              <div className="text-xs text-green-500 mt-1">
                Daily at 5 PM AEST
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="px-4 py-4">
        <div className="max-w-7xl mx-auto grid grid-cols-3 gap-3">
          <div className="stats-card">
            <div className="text-2xl font-bold text-green-500" data-testid="total-recommendations">
              {recommendations.length}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Total Picks</div>
          </div>
          <div className="stats-card">
            <div className="text-2xl font-bold text-green-400">
              {recommendations.filter(r => r.score >= 300).length}
            </div>
            <div className="text-xs text-muted-foreground mt-1">High Score</div>
          </div>
          <div className="stats-card">
            <div className="text-2xl font-bold text-yellow-500">
              {recommendations.filter(r => r.is_most_active).length}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Most Active</div>
          </div>
        </div>

        {/* Search bar */}
        <div className="max-w-7xl mx-auto mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search ticker or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:border-emerald-500/50"
            data-testid="recommendations-search"
          />
        </div>
      </div>

      {/* Recommendations List */}
      <div className="px-4 pb-6">
        <div className="max-w-7xl mx-auto space-y-2">
          {recommendations
            .filter(rec => {
              if (!search) return true;
              const q = search.toLowerCase();
              return (rec.symbol || '').toLowerCase().includes(q) || (rec.name || '').toLowerCase().includes(q);
            })
            .map((rec, index) => (
            <div
              key={`${rec.symbol}-${rec.as_of}-${index}`}
              className="stock-card"
              data-testid={`stock-${rec.symbol}-${index}`}
            >
              <div 
                className="hover:scale-[1.01] transition-transform cursor-pointer"
                onClick={() => navigate(`/stock/${rec.symbol}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-muted-foreground">#{index + 1}</span>
                      <h3 className="text-lg font-semibold">{rec.symbol}</h3>
                      {rec.is_most_active && (
                        <span className="badge badge-blue text-xs">Active</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{rec.name}</p>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Rating:</span>
                        <span className="ml-1 font-semibold">{rec.buy_rating}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Growth:</span>
                        <span className="ml-1 font-semibold text-green-500">
                          {rec.potential_growth_pct}%
                        </span>
                      </div>
                      {rec.price && (
                        <div>
                          <span className="text-muted-foreground">Price:</span>
                          <span className="ml-1 font-semibold">${rec.price.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Add to Portfolio Button - Centered */}
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePortfolio(rec, 'Top Picks'); }}
                    className={`mx-4 p-3 rounded-full transition-all ${
                      isInPortfolio(rec.symbol)
                        ? 'bg-green-500/30 text-green-400 hover:bg-red-500/30 hover:text-red-400'
                        : 'bg-card-hover hover:bg-primary/20 text-muted-foreground hover:text-primary'
                    }`}
                    data-testid={`add-portfolio-${rec.symbol}`}
                    title={isInPortfolio(rec.symbol) ? 'Remove from Portfolio' : 'Add to Portfolio'}
                  >
                    {isInPortfolio(rec.symbol) ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Plus className="w-5 h-5" />
                    )}
                  </button>
                  
                  <div className="text-right min-w-[80px]">
                    <div className={`text-3xl font-bold ${getScoreColor(rec.score)}`} data-testid={`score-${rec.symbol}`}>
                      {rec.score.toFixed(0)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">Score</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {recommendations.length === 0 && (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No recommendations available</p>
              <p className="text-sm text-muted-foreground mt-2">Check back after 5 PM AEST</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RecommendationsPage;
